import React, {useMemo} from 'react';
import block from 'bem-cn-lite';
import {AppContext} from '@app/context';
import {OrderItemModel} from '@features/orders/interfaces';
import {SharedTable, SharedTableColumn, SortOption} from '@frontend/shared';
import {formatDate} from '@utils';
import {useContext} from 'react';
import {useMediaQuery} from 'react-responsive';
import {CaretDown, CaretUp} from '@gravity-ui/icons';
import {Text} from '@gravity-ui/uikit';
import {ArticleInfo, DeliveryDate, Price, ProposalBadges, Quantity} from '@components';
import {Status} from './components/Items/components/Status';
import {CommentWithTooltip} from './components/Items/components/CommentWithTooltip';
import {AuxControls} from './components/Items/components/AuxControls';

import './OrdersItems.scss';

const b = block('orders-items');

interface OrdersItemsProps {
    sortOption: SortOption | null;
    items: OrderItemModel[];
    onSortChange: (field: string) => void;
    stickyOffset: number;
}

export const OrdersItems: React.FC<OrdersItemsProps> = ({
    sortOption,
    items,
    onSortChange,
    stickyOffset
}) => {
    const {state} = useContext(AppContext);
    const {config} = state.orders;
    const {itemsChecked, itemsOrder} = config;

    const isSmallScreen = useMediaQuery({query: '(max-width: 1440px)'});

    const getIconColor = (field: string) => {
        return sortOption?.property === field ? '#BD8E4B' : 'inherit';
    };

    const renderCaretIcon = (field: string) => {
        if (sortOption?.property === field) {
            return sortOption.direction === 'asc' ? (
                <CaretUp style={{color: getIconColor(field)}} />
            ) : (
                <CaretDown style={{color: getIconColor(field)}} />
            );
        } else {
            return <CaretUp style={{color: getIconColor(field)}} />;
        }
    };

    const columnWidths: Record<number, number> = useMemo(() => {
        return isSmallScreen
            ? {
                  1: 98,
                  2: 119,
                  3: 101,
                  4: 124,
                  5: 80,
                  6: 176,
                  7: 82,
                  8: 100,
                  9: 100,
                  10: 128
              }
            : {
                  1: 98,
                  2: 119,
                  3: 136,
                  4: 116,
                  5: 68,
                  6: 241,
                  7: 82,
                  8: 124,
                  9: 154,
                  10: 148
              };
    }, [isSmallScreen]);

    const columnDefinitions: Record<number, SharedTableColumn<OrderItemModel>> = useMemo(
        () => ({
            1: {
                id: 1,
                className: b('block'),
                width: `${columnWidths[1]}px`,
                sortable: true,
                sortField: 'date',
                renderHeader: () => {
                    return (
                        <div className={b('date-order')} onClick={() => onSortChange('date')}>
                            <Text variant="subheader-2">
                                Дата
                                <br />
                                заказа
                            </Text>
                            {renderCaretIcon('date')}
                        </div>
                    );
                },
                renderCell: (item) => (
                    <Text variant={'body-2'}>{formatDate(item.createdAt, 'DD.MM.YY')}</Text>
                )
            },
            2: {
                id: 2,
                className: b('block'),
                width: `${columnWidths[2]}px`,
                sortable: true,
                sortField: 'delivery',
                renderHeader: () => {
                    return (
                        <div
                            className={b('date-delivery')}
                            onClick={() => onSortChange('delivery')}
                        >
                            <Text variant="subheader-2">
                                Дата
                                <br />
                                доставки
                            </Text>
                            {renderCaretIcon('delivery')}
                        </div>
                    );
                },
                renderCell: (item) => (
                    <DeliveryDate
                        value={item.deliveryDate}
                        isCustomOrder={true}
                        className={b('date-delivery-cell')}
                    />
                )
            },
            3: {
                id: 3,
                className: b('block'),
                width: `${columnWidths[3]}px`,
                sortable: true,
                sortField: 'status_date',
                renderHeader: () => {
                    return (
                        <div className={b('status')} onClick={() => onSortChange('status_date')}>
                            <Text variant="subheader-2">Статус</Text>
                            {renderCaretIcon('status_date')}
                        </div>
                    );
                },
                renderCell: (item) => <Status item={item} />
            },
            4: {
                id: 4,
                className: b('block'),
                width: `${columnWidths[4]}px`,
                sortable: true,
                sortField: 'ref_id',
                renderHeader: () => {
                    return (
                        <div className={b('ref')} onClick={() => onSortChange('ref_id')}>
                            <Text variant="subheader-2">Реф №</Text>
                            {renderCaretIcon('ref_id')}
                        </div>
                    );
                },
                renderCell: (item) => (
                    <div className={b('ref-cell')}>
                        <Text variant={'body-2'}>{item.refId}</Text>
                    </div>
                )
            },
            5: {
                id: 5,
                className: b('block'),
                width: `${columnWidths[5]}px`,
                renderHeader: () => {
                    return <div className={b('bages')}></div>;
                },
                renderCell: (item) => {
                    const isOfficialDealer = !!item.flags?.official;
                    const isReturnPossible = item.return !== 'impossible';

                    return (
                        <ProposalBadges
                            isOfficialDealer={isOfficialDealer}
                            isReturnPossible={isReturnPossible}
                            className={b('icons-cell')}
                        />
                    );
                }
            },
            6: {
                id: 6,
                className: b('block'),
                width: `${columnWidths[6]}px`,
                renderHeader: () => {
                    return (
                        <div className={b('article')}>
                            <Text variant="subheader-2">Наименование</Text>
                        </div>
                    );
                },
                renderCell: (item) => (
                    <ArticleInfo
                        header={{
                            article: item.article,
                            articleName: item.articleName,
                            brandName: item.brandName
                        }}
                        isCustomOrder={true}
                        className={b('article-cell')}
                    />
                )
            },
            7: {
                id: 7,
                className: b('block'),
                width: `${columnWidths[7]}px`,
                renderHeader: () => {
                    return (
                        <div className={b('quantity')}>
                            <Text variant="subheader-2">
                                Кол-во,
                                <br />
                                шт.
                            </Text>
                        </div>
                    );
                },
                renderCell: (item) => (
                    <Quantity value={item.quantity} className={b('quantity-cell')} />
                )
            },
            8: {
                id: 8,
                className: b('block'),
                width: `${columnWidths[8]}px`,
                sortable: true,
                sortField: 'price',
                renderHeader: () => {
                    return (
                        <div className={b('price')} onClick={() => onSortChange('price')}>
                            <Text variant="subheader-2">Цена</Text>
                            {renderCaretIcon('price')}
                        </div>
                    );
                },
                renderCell: (item) => <Price value={item.price} className={b('price-cell')} />
            },
            9: {
                id: 9,
                className: b('block'),
                width: `${columnWidths[9]}px`,
                sortable: true,
                sortField: 'sum',
                renderHeader: () => {
                    return (
                        <div className={b('sum')} onClick={() => onSortChange('sum')}>
                            <Text variant="subheader-2">Сумма заказа</Text>
                            {renderCaretIcon('sum')}
                        </div>
                    );
                },
                renderCell: (item) => <Price value={item.sum} className={b('sum-cell')} />
            },
            10: {
                id: 10,
                className: b('block'),
                width: `${columnWidths[10]}px`,
                renderHeader: () => {
                    return (
                        <div className={b('comment')}>
                            <Text variant="subheader-2">Комментарий</Text>
                        </div>
                    );
                },
                renderCell: (item) =>
                    item.comment ? (
                        <CommentWithTooltip comment={item.comment} />
                    ) : (
                        <Text variant={'body-1'} color="hint">
                            Пусто
                        </Text>
                    )
            }
        }),
        [b, columnWidths, sortOption]
    );

    const visibilityConfig = {
        itemsChecked,
        itemsOrder
    };

    // Дополнительные колонки, не входящие в itemsOrder
    const additionalColumns: Record<number, SharedTableColumn<OrderItemModel>> = useMemo(
        () => ({
            11: {
                id: 11,
                width: '88px',
                renderHeader: () => {
                    return <div className={b('aux-controls')}></div>;
                },
                renderCell: (item) => {
                    const isReturnPossible = item.return !== 'impossible';

                    return <AuxControls isReturnPossible={isReturnPossible} item={item} />;
                }
            }
        }),
        []
    );

    return (
        <div className={b()}>
            <SharedTable
                data={items}
                columnDefinitions={columnDefinitions}
                visibilityConfig={visibilityConfig}
                columnWidths={columnWidths}
                sortOption={sortOption}
                onSortChange={onSortChange}
                stickyOffset={stickyOffset}
                className={b('table')}
                headerClassName={b('header')}
                bodyClassName={b('body')}
                rowBodyClassName={b('row')}
                additionalColumns={additionalColumns}
                alwaysVisibleColumnId={6}
            />
        </div>
    );
};
